import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { EMPTY, Subject } from "rxjs";
import { debounceTime, switchMap } from "rxjs/operators";

import { EvaService } from "src/app/core/services/eva.service";
import { EvaInputComponent } from "src/app/shared/ds/eva-input/eva-input.component";

import { type TagPreset } from "src/app/shared/constants/tag-presets";

import { Task, TaskObjectLink, TaskUrgency } from "../domains/task";
import { TaskService } from "../services/task.service";
import { AssigneeItem } from "../task-assignee-picker/task-assignee-picker.component";
import { NoteService } from "src/app/services/note.service";
import { AttachmentSearchGroup } from "src/app/domains/note";
import { onDocumentClickCapture } from "src/app/shared/ds/utils/listen-outside-angular";

@Component({
  selector: "app-task-inline-edit",
  templateUrl: "./task-inline-edit.component.html",
  styleUrls: ["./task-inline-edit.component.scss"],
})
export class TaskInlineEditComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  /** Tâche à éditer */
  @Input({ required: true }) task: Task;
  /** Liste des employés pour l'assignation */
  @Input() employees: any[] = [];

  /** Émis après mise à jour réussie */
  @Output() taskUpdated = new EventEmitter<Task>();
  /** Émis quand l'utilisateur annule */
  @Output() cancelled = new EventEmitter<void>();
  /** Émis après suppression réussie */
  @Output() deleted = new EventEmitter<void>();

  @ViewChild("titleInput") titleInput: EvaInputComponent;

  // Champs du formulaire (initialisés depuis la tâche)
  title = "";
  dueDate: Date = null;
  plannedStartDate: Date = null;
  urgency: TaskUrgency = TaskUrgency.NORMAL;
  isPrivate = false;
  assignedTo: AssigneeItem[] = [];

  // Mode date
  dateMode: "none" | "deadline" | "range" = "none";

  readonly dateModeOptions = [
    { label: "Aucune", value: "none" },
    { label: "Échéance", value: "deadline" },
    { label: "Période", value: "range" },
  ];

  activeObjectsLinks: TaskObjectLink[] = [];
  saving = false;
  titleError = "";

  // Lier à
  showLinkSearch = false;
  linkSearchQuery = "";
  linkSearchResults: AttachmentSearchGroup[] = [];
  isSearchingLinks = false;
  private linkSearch$ = new Subject<string>();
  private elementRef = inject(ElementRef);
  private noteService = inject(NoteService);

  onDocumentClick(event: Event): void {
    if (this.showLinkSearch && !this.elementRef.nativeElement.contains(event.target)) {
      this.showLinkSearch = false;
      this.linkSearchQuery = "";
      this.linkSearchResults = [];
    }
  }

  /** Clic à l'extérieur, en capture : le galet arrête la propagation des clics */
  private readonly stopOutsideClick = onDocumentClickCapture(
    () => this.showLinkSearch,
    (event) => this.onDocumentClick(event),
  );

  constructor(
    private taskService: TaskService,
    public evaService: EvaService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.populateFromTask();
    this.focusTitle();
    this.initLinkSearch();
  }

  /** Hydrate le formulaire depuis la tâche existante */
  private populateFromTask(): void {
    if (!this.task) return;
    this.title = this.task.title || "";
    this.dueDate = this.task.due_date ? new Date(this.task.due_date) : null;
    this.plannedStartDate = this.task.planned_start_date ? new Date(this.task.planned_start_date) : null;
    this.dateMode = this.resolveDateMode(this.plannedStartDate, this.dueDate);
    this.urgency = this.task.urgency || TaskUrgency.NORMAL;
    this.isPrivate = this.task.is_private || false;
    this.assignedTo = (this.task.assigned_to || []).map((a) =>
      a.type === "employee"
        ? { type: "employee" as const, employee_id: a.employee_id, name: a.name }
        : { type: "external" as const, name: a.name },
    );
    this.activeObjectsLinks = [...(this.task.objects_links || [])];
  }

  private focusTitle(): void {
    this.cdr.detectChanges();
    setTimeout(() => {
      this.titleInput?.focus();
    }, 50);
  }

  private validate(): boolean {
    this.titleError = "";
    if (!this.title || this.title.trim() === "") {
      this.titleError = "Le nom est obligatoire";
      return false;
    }
    return true;
  }

  submit(): void {
    if (!this.validate()) return;
    if (this.saving) return;

    this.saving = true;

    const payload: any = {
      title: this.title.trim(),
      urgency: this.urgency,
      assigned_to: this.assignedTo.map((a) =>
        a.type === "employee" ? { type: "employee", employee_id: a.employee_id } : { type: "external", name: a.name },
      ),
      objects_links: this.activeObjectsLinks,
      is_private: this.isPrivate,
      due_date: this.dueDate || null,
      planned_start_date: this.plannedStartDate || null,
    };

    this.taskService
      .updateTask(this.task.task_id, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated: Task) => {
          this.saving = false;
          this.taskUpdated.emit(updated);
        },
        error: () => {
          this.evaService.showError("Impossible de modifier la tâche", "updateTask", "");
          this.saving = false;
          this.cdr.markForCheck();
        },
      });
  }

  cancel(): void {
    this.cancelled.emit();
  }

  deleteTask(): void {
    if (!confirm("Supprimer cette tâche ?")) return;
    this.taskService
      .deleteTask(this.task.task_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.evaService.showSuccess("Tâche supprimée");
          this.deleted.emit();
        },
        error: () => {
          this.evaService.showError("Impossible de supprimer la tâche", "deleteTask", "");
        },
      });
  }

  private resolveDateMode(start: Date | null, end: Date | null): "none" | "deadline" | "range" {
    if (start != null && end != null) return "range";
    if (end != null) return "deadline";
    return "none";
  }

  onDateModeChange(mode: "none" | "deadline" | "range"): void {
    this.dateMode = mode;
    if (mode === "none") {
      this.dueDate = null;
      this.plannedStartDate = null;
    } else if (mode === "deadline") {
      this.plannedStartDate = null;
    }
  }

  onRangeDateChange(value: Date | Date[] | null): void {
    if (Array.isArray(value)) {
      this.plannedStartDate = value[0] || null;
      this.dueDate = value[1] || null;
    } else {
      this.plannedStartDate = null;
      this.dueDate = null;
    }
  }

  get rangeDateValue(): Date[] | null {
    if (this.plannedStartDate || this.dueDate) {
      return [this.plannedStartDate, this.dueDate].filter(Boolean) as Date[];
    }
    return null;
  }

  private initLinkSearch(): void {
    this.linkSearch$
      .pipe(
        debounceTime(300),
        switchMap((query) => {
          if (query.length < 2) {
            this.linkSearchResults = [];
            this.isSearchingLinks = false;
            return EMPTY;
          }
          this.isSearchingLinks = true;
          return this.noteService.searchAttachments(query);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (groups) => {
          this.linkSearchResults = groups || [];
          this.isSearchingLinks = false;
        },
        error: () => {
          this.linkSearchResults = [];
          this.isSearchingLinks = false;
        },
      });
  }

  toggleLinkSearch(): void {
    this.showLinkSearch = !this.showLinkSearch;
    if (!this.showLinkSearch) {
      this.linkSearchQuery = "";
      this.linkSearchResults = [];
    }
  }

  onLinkSearch(value: string): void {
    this.linkSearchQuery = value;
    this.linkSearch$.next(value);
  }

  addObjectLink(objectType: string, objectId: string, label: string): void {
    if (this.activeObjectsLinks.some((l) => l.object_type === objectType && l.object_id === objectId)) return;
    this.activeObjectsLinks = [
      ...this.activeObjectsLinks,
      { object_type: objectType, object_id: objectId, object_name: label },
    ];
    this.showLinkSearch = false;
    this.linkSearchQuery = "";
    this.linkSearchResults = [];
  }

  removeObjectLink(objectType: string, objectId: string): void {
    this.activeObjectsLinks = this.activeObjectsLinks.filter(
      (l) => !(l.object_type === objectType && l.object_id === objectId),
    );
  }

  get privateTagPreset(): TagPreset {
    return { label: "Privée", severity: "secondary", icon: "heroLockClosed" };
  }

  togglePrivate(): void {
    this.isPrivate = !this.isPrivate;
  }

  onTitleKeyDown(event: KeyboardEvent): void {
    if (event.key === "Enter") {
      event.preventDefault();
      this.submit();
    }
    if (event.key === "Escape") {
      event.preventDefault();
      // Titre modifié : Échap quitte juste le champ, la saisie n'est pas perdue
      if (this.title.trim() !== (this.task?.title || "").trim()) {
        (event.target as HTMLElement | null)?.blur();
        return;
      }
      this.cancel();
    }
  }
}
